import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Heart, Scissors, Wallet, BarChart3,
  PieChart, FileText, Settings as SettingsIcon, LogOut, Menu, X, CalendarDays, Filter, Tag, Sparkles, PhoneCall, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Iteration 30e: each entry declares the permission it requires.
// Layout filters entries by `user.permissions` (returned from /api/auth/me,
// with permission_overrides from Settings honoured). If `perm` is null → visible to all.
const NAV = [
  { to: "/", label: "Дашборд", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard" },
  { to: "/operational-days", label: "Операційні дні", icon: CalendarDays, testid: "nav-operational-days", perm: "operations.read" },
  { to: "/operations", label: "Журнал операцій", icon: Scissors, testid: "nav-operations", perm: "operations.read" },
  { to: "/pricelist", label: "Прейскурант", icon: Tag, testid: "nav-pricelist", perm: "pricelist.read" },
  { to: "/patients", label: "Пацієнти", icon: Heart, testid: "nav-patients", perm: "patients.read" },
  { to: "/funnel", label: "Воронка", icon: Filter, testid: "nav-funnel", perm: "patients.read" },
  { to: "/recommendations", label: "Рекомендації", icon: Sparkles, testid: "nav-recommendations", perm: "recommendations.read" },
  { to: "/calls", label: "Дзвінки", icon: PhoneCall, testid: "nav-calls", perm: "patients.write" },
  { to: "/employees", label: "Працівники", icon: Users, testid: "nav-employees", perm: "employees.read" },
  { to: "/payments", label: "Виплати", icon: Wallet, testid: "nav-payments", perm: "payments.read" },
  { to: "/kpi", label: "KPI", icon: BarChart3, testid: "nav-kpi", perm: "kpi.read" },
  { to: "/finances", label: "Фінанси", icon: PieChart, testid: "nav-finances", perm: "finances.read" },
  { to: "/reports", label: "Звіти", icon: FileText, testid: "nav-reports", perm: "reports.read" },
  { to: "/instructions", label: "Інструкції", icon: BookOpen, testid: "nav-instructions", perm: null },
  { to: "/settings", label: "Налаштування", icon: SettingsIcon, testid: "nav-settings", perm: "settings.read" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Global Vision" className="h-14 w-14 shrink-0 object-contain" />
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Global Vision</div>
              <div className="heading font-bold text-[#0F2027] text-lg leading-none">Analytics</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.filter((item) => !item.perm || user?.permissions?.includes(item.perm)).map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setMobileOpen(false)}
                className={`gv-sidebar-link ${active ? "active" : ""}`}
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#0D9488] text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#0F2027] truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.role_label || user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Вийти
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 h-16 flex items-center px-4 lg:px-8 justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 -ml-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">GV Analytics</div>
              <div className="heading text-[#0F2027] font-semibold">
                Аналітика, операції, фінанси та управління персоналом
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Система працює
          </div>
        </header>

        <main data-testid="main-content" className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
